import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore.js";
import { useI18n } from "../i18n/index.js";
import type { TranslationKey } from "../i18n/index.js";
import { fetchDailyStats, type DailyStat } from "../lib/scores.js";
import type { DrawMode } from "@solitaire/game-engine";

// ============================================================
// Types
// ============================================================

type ModeId = "daily" | "klondike" | "journey" | "weekly" | "tower";

interface ModeTab {
  id: ModeId;
  labelKey: TranslationKey;
  enabled: boolean;
}

const MODES: ModeTab[] = [
  { id: "daily",    labelKey: "profile.daily",    enabled: true },
  { id: "klondike", labelKey: "profile.klondike", enabled: true },
  { id: "journey",  labelKey: "profile.journey",  enabled: false },
  { id: "weekly",   labelKey: "profile.weekly",   enabled: false },
  { id: "tower",    labelKey: "profile.tower",    enabled: false },
];

// ============================================================
// Kalender-Hilfsfunktionen
// ============================================================

/** Anzahl Tage im Monat */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate(); // month is 1-based here
}

/** Wochentag des 1. im Monat (0=Mo, 1=Di, …, 6=So) */
function firstWeekday(year: number, month: number): number {
  const day = new Date(year, month - 1, 1).getDay(); // 0=So, 1=Mo, …
  return day === 0 ? 6 : day - 1; // umrechnen: Mo=0 … So=6
}

/** Monatsname lokalisiert */
function monthName(year: number, month: number, locale: string): string {
  return new Date(year, month - 1, 1).toLocaleString(locale, { month: "long" });
}

// ============================================================
// DailyCalendar
// ============================================================

function DailyCalendar({ userId, drawMode = 1 }: { userId: string; drawMode?: DrawMode }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-based
  const [stats, setStats] = useState<DailyStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchDailyStats(userId, year, month, drawMode)
      .then((data) => { setStats(data); })
      .catch(() => { setStats([]); })
      .finally(() => { setLoading(false); });
  }, [userId, year, month, drawMode]);

  const days = daysInMonth(year, month);
  const offset = firstWeekday(year, month);
  const todayDay = today.getFullYear() === year && today.getMonth() + 1 === month ? today.getDate() : -1;

  // Map: day → won/lost
  const statMap = new Map<number, boolean>();
  for (const s of stats) {
    statMap.set(s.day, s.won);
  }

  function prevMonth() {
    if (month === 1) { setYear(year - 1); setMonth(12); }
    else { setMonth(month - 1); }
  }

  function nextMonth() {
    if (month === 12) { setYear(year + 1); setMonth(1); }
    else { setMonth(month + 1); }
  }

  // Wochentag-Header (lokalisiert, kurz)
  const weekdays = locale.startsWith("de")
    ? ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]
    : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="w-full max-w-sm mx-auto">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="px-3 py-1 text-white/60 hover:text-white/90 transition-colors text-lg"
        >
          ‹
        </button>
        <span className="text-white font-semibold capitalize">
          {monthName(year, month, locale)} {year}
        </span>
        <button
          onClick={nextMonth}
          className="px-3 py-1 text-white/60 hover:text-white/90 transition-colors text-lg"
        >
          ›
        </button>
      </div>

      {/* Wochentag-Header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdays.map((wd) => (
          <div key={wd} className="text-center text-[11px] text-white/40 font-medium py-1">
            {wd}
          </div>
        ))}
      </div>

      {/* Tage */}
      {loading ? (
        <div className="text-center text-white/40 py-8 text-sm">{t("profile.noData")}</div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {/* Leere Zellen vor dem 1. */}
          {Array.from({ length: offset }, (_, i) => (
            <div key={`empty-${i}`} />
          ))}

          {/* Tage 1…N */}
          {Array.from({ length: days }, (_, i) => {
            const day = i + 1;
            const stat = statMap.get(day);
            const isToday = day === todayDay;

            // Farben
            let bgColor = "bg-white/5";       // nicht gespielt
            let textColor = "text-white/40";
            if (stat === true) {
              bgColor = "bg-green-500/50";     // gewonnen
              textColor = "text-white";
            } else if (stat === false) {
              bgColor = "bg-red-500/50";       // verloren
              textColor = "text-white";
            }

            const seed = year * 10000 + month * 100 + day;

            return (
              <div
                key={day}
                onClick={() => navigate(`/leaderboard?seed=${seed}${drawMode === 3 ? "&drawMode=3" : ""}`)}
                className={`
                  aspect-square flex items-center justify-center rounded-md text-sm font-medium
                  cursor-pointer hover:ring-2 hover:ring-white/40 transition-all
                  ${bgColor} ${textColor}
                  ${isToday ? "ring-2 ring-white/70" : ""}
                `}
              >
                {day}
              </div>
            );
          })}
        </div>
      )}

      {/* Legende */}
      <div className="flex items-center justify-center gap-4 mt-4 text-[11px] text-white/50">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-green-500/50 inline-block" />
          {t("profile.won")}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-red-500/50 inline-block" />
          {t("profile.lost")}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-white/5 inline-block" />
          {t("profile.notPlayed")}
        </span>
      </div>
    </div>
  );
}

// ============================================================
// ProfilePage
// ============================================================

export function ProfilePage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const user = useAuthStore((s) => s.user);
  const [activeMode, setActiveMode] = useState<ModeId>("daily");

  // Guard: nur für eingeloggte User
  useEffect(() => {
    if (!user) navigate("/login", { replace: true });
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[var(--table-bg)] text-white">
      <div className="flex flex-col items-center px-4 py-6 gap-6 max-w-lg mx-auto">
        {/* Header */}
        <div className="w-full flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white/90 transition-colors text-sm"
          >
            {t("profile.back")}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold uppercase select-none">
              {user.username[0]}
            </div>
            <span className="text-white font-semibold">{user.username}</span>
          </div>
        </div>

        {/* Titel */}
        <h1 className="text-white text-xl font-bold tracking-wide">
          {t("profile.title")}
        </h1>

        {/* Modi-Tabs */}
        <div className="w-full flex flex-wrap gap-2 justify-center">
          {MODES.map((mode) => (
            <button
              key={mode.id}
              disabled={!mode.enabled}
              onClick={() => mode.enabled && setActiveMode(mode.id)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${mode.id === activeMode
                  ? "bg-white/20 text-white"
                  : mode.enabled
                    ? "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80"
                    : "bg-white/5 text-white/25 cursor-not-allowed"
                }
              `}
            >
              {t(mode.labelKey)}
              {!mode.enabled && (
                <span className="ml-1.5 text-[9px] uppercase tracking-wider opacity-60">
                  ({t("profile.comingSoon")})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="w-full">
          {activeMode === "daily" && <DailyCalendar userId={user.id} />}
          {activeMode === "klondike" && <DailyCalendar userId={user.id} drawMode={3} />}

          {activeMode !== "daily" && activeMode !== "klondike" && (
            <div className="text-center text-white/30 py-12 text-sm">
              {t("profile.comingSoon")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
